/**
 * How long a manual relay tap may run and how long it waits for answers, and its colours.
 * Split out of pages/irrigation/EspNowControllerDetail.tsx.
 */
import { type RelayTone } from '../../../utils/relayCommand';

// Manual relay taps are for testing a valve or pump, nothing more. One tap turns
// it on, the next tap sends CLEAR_RELAY and stops it — but the SET_RELAY window
// is deliberately short so a mistaken tap, or one nobody comes back to, cannot
// leave water running. The tap-again path is the normal way to stop; this is
// only the backstop.
export const MANUAL_RUN_MAX_SEC = 60;
export const MANUAL_RUN_MAX_LABEL = '60 seconds';

// How long a tap waits for the SITE to say it carried the command out. The
// bridge collects its outbox about every 15 s and retries a node up to five
// times before giving up, so an honest refusal can take most of a minute to
// arrive — and a refusal is the answer most worth waiting for.
export const RELAY_ACK_WAIT_MS = 60_000;
// And then for the NODE to report the output. The bridge drains its outbox at
// the start of a poll tick and runs commands at the end of one, so a reading
// reaches the server a tick after the ack announcing it.
export const RELAY_READ_WAIT_MS = 60_000;
export const RELAY_POLL_MS = 3_000;

export const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

/**
 * One colour per claim, and the claims are ranked by who is making them.
 *
 * Green is the node's own word: it reported this output on. Amber is ours — we
 * asked and the site says it obliged, which is not the same thing and never was.
 * Red is a refusal or a contradiction. Grey is off, on the same evidence green
 * is on.
 *
 * A master pump keeps its identity in the BORDER rather than the fill, so the
 * fill is free to mean one thing everywhere.
 */
export const RELAY_TONE: Record<RelayTone, string> = {
  on: 'bg-green-500 text-white hover:bg-green-600',
  commanded: 'bg-amber-400 text-white hover:bg-amber-500',
  failed: 'bg-red-500 text-white hover:bg-red-600',
  busy: 'bg-gray-100 border border-gray-300 text-gray-500',
  off: 'bg-gray-50 border border-gray-200 text-gray-500 hover:bg-gray-100',
};
